// ============================================================================
// Ask AI's mileage card — "record my mileage" (item #1011, Ben "Vehicle
// Decision: A"). The server half is propose_mileage in
// supabase/functions/am-copilot/fleet.ts. The save is the driver tile's own
// (fleet/hooks/use-fleet-data.ts submitOdometer); the database copies the
// reading onto the vehicle.
// ============================================================================

import { submitOdometer } from '../fleet/hooks/use-fleet-data';
import { rpcErrorMessage } from '../../shared/rpc-error';

/** A draft as the server persisted it (prepareMileage). */
export interface MileageDraft {
  vehicle_id: string;
  odometer: number;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const ctxOf = (d: MileageDraft) => (d.context ?? {}) as Record<string, unknown>;
const miles = (n: unknown) => (n == null || n === '' ? '—' : `${Number(n).toLocaleString('en-US')} mi`);

/** The vehicle as the fleet screens name it. */
export const vehicleOf = (d: MileageDraft) => str(ctxOf(d).vehicle) || str(d.display_name) || d.vehicle_id;

/** What the card shows, with the person's edited reading `odometer`. */
export function mileageCard(d: MileageDraft, odometer: unknown) {
  const c = ctxOf(d);
  const n = Number(odometer);
  const last = c.last_mileage == null ? null : Number(c.last_mileage);
  const missing = !Number.isFinite(n) || n <= 0
    ? 'Enter the odometer reading.'
    : last != null && n < last
      ? `That's below the last reading (${last.toLocaleString('en-US')}). Check the number.`
      : null;
  return {
    title: `Odometer ${miles(n)}`,
    info: [
      { label: 'Last reading', value: `${miles(last)}${str(c.last_mileage_at) ? ` · ${str(c.last_mileage_at)}` : ''}` },
    ],
    doItLabel: 'Save reading',
    missing,
  };
}

export interface MileageCommitResult { ok: boolean; error?: string; note?: string; target_row_id?: string | null }

/** Save the reading through the driver tile's own function. */
export async function commitMileage(d: MileageDraft, odometer: unknown, eid: string | null): Promise<MileageCommitResult> {
  const card = mileageCard(d, odometer);
  if (card.missing) return { ok: false, error: card.missing };
  try {
    await submitOdometer(d.vehicle_id, Math.trunc(Number(odometer)), eid);
    return { ok: true, note: `${vehicleOf(d)} — odometer ${miles(odometer)} saved.`, target_row_id: d.vehicle_id };
  } catch (e) {
    return { ok: false, error: rpcErrorMessage(e, 'Could not save the reading.') };
  }
}
